<script setup lang="ts">
/**
 * What this family has coming, and what it has brought (SPEC §8.7, §6.30,
 * MT-105).
 *
 * The question it answers is "did we bring the salad last time". Without it
 * families repeat themselves, or everybody brings a dessert because nobody can
 * remember who did it in July.
 *
 * Informational, and only that. Nothing here can be changed from here — every
 * entry links to the meal, which is where answering and claiming happen.
 */
import { computed, ref } from 'vue'
import PatientLoader from '@/components/PatientLoader.vue'
import EmptyState from '@/components/EmptyState.vue'
import { endpoints, type FamilyMealHistoryResponse } from '@/api'
import { describeWhen } from '@/utils/dates'

const upcoming = ref<FamilyMealHistoryResponse[]>([])
const past = ref<FamilyMealHistoryResponse[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

const nothingAtAll = computed(() => upcoming.value.length === 0 && past.value.length === 0)

/** Plain language, never the enum (SPEC §9.1). */
const ANSWERS: Record<string, string> = {
  Yes: 'You said yes',
  No: 'You said no',
  Maybe: 'You said maybe',
}

function answer(meal: FamilyMealHistoryResponse): string {
  if (meal.status === 'Cancelled') return 'Called off'
  if (!meal.rsvpStatus) return 'You did not answer'

  const said = ANSWERS[meal.rsvpStatus] ?? 'You answered'
  const count = meal.attendingCount

  return meal.rsvpStatus === 'Yes' && typeof count === 'number' && count > 0
    ? `${said} — ${count} of you`
    : said
}

function when(meal: FamilyMealHistoryResponse): string {
  // describeWhen returns null for a date it cannot parse. Falling back to the
  // raw date is worse than nothing dressed up as a date nobody can read.
  return describeWhen(meal.localDate, meal.localTime, new Date())?.absolute ?? meal.localDate
}

async function load(): Promise<void> {
  loading.value = true
  error.value = null

  try {
    const history = await endpoints.familyHistory()

    upcoming.value = history.upcoming
    past.value = history.past
  } catch {
    error.value = 'We could not load your meals just then. Try again in a moment.'
  } finally {
    loading.value = false
  }
}

void load()
</script>

<template>
  <div class="history">
    <h1 class="history__title">Your meals</h1>

    <v-alert v-if="error" type="error" variant="tonal">{{ error }}</v-alert>

    <PatientLoader v-if="loading" label="your meals" @retry="load" />

    <template v-else>
      <EmptyState
        v-if="nothingAtAll && !error"
        icon="mdi-calendar-blank-outline"
        headline="Nothing here yet"
        description="Meals you are invited to will show up here, along with what you brought to them."
      />

      <section v-if="upcoming.length > 0" class="history__group">
        <h2 class="history__heading">Coming up</h2>

        <RouterLink
          v-for="meal in upcoming"
          :key="meal.eventId"
          class="history__row"
          :to="{ name: 'family-event', params: { eventId: meal.eventId } }"
          data-testid="upcoming-meal"
        >
          <span class="history__name">{{ meal.title }}</span>
          <span class="history__when">{{ when(meal) }}</span>
          <span class="history__answer">{{ answer(meal) }}</span>
          <span v-if="meal.brought.length > 0" class="history__brought">
            Bringing {{ meal.brought.join(', ') }}
          </span>
        </RouterLink>
      </section>

      <section v-if="past.length > 0" class="history__group">
        <h2 class="history__heading">Before</h2>

        <RouterLink
          v-for="meal in past"
          :key="meal.eventId"
          class="history__row"
          :to="{ name: 'family-event', params: { eventId: meal.eventId } }"
          data-testid="past-meal"
        >
          <span class="history__name">{{ meal.title }}</span>
          <span class="history__when">{{ when(meal) }}</span>
          <span class="history__answer">{{ answer(meal) }}</span>
          <!--
            The whole reason this screen exists. Past tense, because a meal
            that has happened is a record rather than a commitment.
          -->
          <span v-if="meal.brought.length > 0" class="history__brought">
            You brought {{ meal.brought.join(', ') }}
          </span>
        </RouterLink>
      </section>
    </template>
  </div>
</template>

<style scoped>
.history {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.history__title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.history__group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.history__heading {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.history__row {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid rgb(var(--v-theme-surface-variant));
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
}

.history__name {
  font-weight: 600;
}

.history__when,
.history__answer {
  font-size: 0.875rem;
  opacity: 0.75;
}

.history__brought {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>
